import { ListItem } from 'app/overview/triggers/builder_interfaces';
import { FieldSchemaUnits, StatDefinition } from 'app/shared/collections/StatDefinition';
import { View } from 'app/shared/collections/view';
import { AGGREGATE_TYPE_COUNT } from 'app/shared/info_box/components/heatmap/heatmap_constants';
import Decimal from 'decimal.js';
import * as _ from 'lodash';

export class UnitService {
    public static isUnit(units: FieldSchemaUnits, desiredUnit: string): boolean {
        if (units) {
            const { suffix, values } = units;

            if (values && values.length > 0) {
                let found = false;

                values.forEach((v) => {
                    if (v.suffix == desiredUnit) {
                        found = true;
                    }
                });

                return found;
            } else if (suffix) {
                return suffix == desiredUnit;
            }
        }

        return false;
    }

    public static getDropdownOptions(units: FieldSchemaUnits): ListItem[] {
        if (units) {
            const newUnits = UnitService.assumeMaxValues(units);
            const { suffix, values } = newUnits;

            if (values && values.length > 0) {
                return values.map((v) => ({
                    display: v.suffix,
                    value: v.suffix,
                }));
            } else if (suffix) {
                return [
                    {
                        display: suffix,
                        value: suffix,
                    },
                ];
            }
        }

        return [];
    }

    public static hasMultipleUnits(units: FieldSchemaUnits): boolean {
        if (units) {
            const { suffix, values } = units;

            if (values && values.length > 1) {
                return true;
            } else if (suffix) {
                return false;
            }
        }

        return false;
    }

    /**
     * Like get base suffix, except it will return null if there's no unit with a 1 multiplier
     *
     */
    public static getNoMultiplierUnitSuffix(units: FieldSchemaUnits): string | null {
        if (!units) {
            return null;
        }
        if (units.suffix && (units.multiplier === 1 || _.isNil(units.multiplier))) {
            // if we have a base unit with a 1 multiplier (or null multiplier), use that
            return units.suffix;
        }
        // return the suffix of the first unit with a null or 1 multiplier
        return (
            units.values?.find((u) => _.isNil(u.multiplier) || u.multiplier === 1)?.suffix || null
        );
    }

    public static getBaseSuffix(units: FieldSchemaUnits): string {
        if (units) {
            const newUnits = UnitService.assumeMaxValues(units);
            const { suffix, values } = newUnits;

            if (values && values.length > 0) {
                const base = values.find((v) => v.multiplier == 1);
                if (base) {
                    return base.suffix;
                } else {
                    return values[0].suffix;
                }
            } else if (suffix) {
                return suffix;
            }
        }

        return '';
    }

    public static getBestUnit(units: FieldSchemaUnits, desiredUnit: string) {
        /*
        Picks a unit set {suffix, multiplier} from the given list of units.
        Tries to find the best match
         - by first picking the unit with the requested suffix
         - otherwise picking the unit with 1x multiplier (base unit)
         - otherwise picking the first available unit
        */
        if (!units) {
            return null;
        }

        const { suffix, values } = units;

        if (values) {
            // our best option - the requested one
            for (const v of values) {
                if (v.suffix == desiredUnit) {
                    return v;
                }
            }

            // 2nd best option - the 1x multiplier
            for (const v of values) {
                if (v.multiplier == 1) {
                    return v;
                }
            }

            // 3rd choice - the "first" one!
            if (values.length > 0) {
                const newUnits = UnitService.assumeMaxValues(units);
                return newUnits['values'][0];
            }
            // 4th choice - the "base" unit
        } else if (!_.isNil(suffix)) {
            return {
                suffix,
                multiplier: UnitService.defaultMultiplier(units.multiplier),
            };
        }

        // If we have no units or suffix (as they are both optional fields in the type) return null also.
        return null;
    }

    public static forceNormalize(
        units: FieldSchemaUnits,
        value: string | number | Date,
        displayUnit: string,
    ): string | number | Date {
        if (typeof value !== 'number') {
            return value;
        }

        let convertedValue = new Decimal(value);

        if (units) {
            if (units.values && units.values.length > 0) {
                const unitGroup = UnitService.getBestUnit(units, displayUnit);
                const multiplier = UnitService.defaultMultiplier(unitGroup.multiplier);
                convertedValue = convertedValue.times(new Decimal(multiplier));
            }
        }

        const precision = UnitService.getPrecision(convertedValue.toNumber());
        return parseFloat(convertedValue.toPrecision(precision));
    }

    public static forceConvert(
        units: FieldSchemaUnits,
        value: string | number | Date,
        displayUnit: string,
    ): string | number | Date {
        if (typeof value !== 'number') {
            return value;
        }

        let convertedValue = new Decimal(value);

        if (units && units.values && units.values.length > 0) {
            const index = units.values.findIndex((d) => d.suffix === displayUnit);
            if (index === -1) {
                return value;
            }
            const unitGroup = units.values[index];
            const multiplier = UnitService.defaultMultiplier(unitGroup.multiplier);
            convertedValue = convertedValue.dividedBy(new Decimal(multiplier));
        }

        const precision = UnitService.getPrecision(convertedValue.toNumber());
        return parseFloat(convertedValue.toPrecision(precision));
    }

    public static convertValue(
        units: FieldSchemaUnits,
        value: string | number,
        suffix: string = '',
        aggregationType?: string,
        decimals: number = null,
    ): { value: string | number; unit: string } {
        // straight away sort out this strange default of empty string
        suffix = suffix || '';
        const defaultValue = { value, unit: suffix };

        if (
            value == null ||
            typeof value === 'string' ||
            aggregationType === AGGREGATE_TYPE_COUNT
        ) {
            return defaultValue;
        }

        // if we have no values, but do have a single suffix
        //  then lift that into a values list for consistency/simplicity
        const isSingleSuffixAndNoValuesList = !units?.values?.length && units?.suffix;
        if (isSingleSuffixAndNoValuesList) {
            units.values = [
                {
                    suffix: units.suffix,
                    multiplier: units.multiplier,
                },
            ];
        }

        // if we still have no values, bail, invalid unit schema
        const hasNoValues = !units?.values?.length;
        if (hasNoValues) {
            return defaultValue;
        }

        // use the absolute value, so we can check max_value only in the positive
        //  realm and add the sign on the way back out.
        const isNegative = value < 0;
        let baseValue = new Decimal(value).abs();
        if (suffix) {
            // if we were told what suffix our value is in, we want to recalculate
            //  our base value according to the multiplier of the suffix we came in with
            const matchingUnitGroup = units.values.find((u) => u.suffix === suffix);
            if (!matchingUnitGroup) {
                // our input value is in an invalid unit, we don't know what it is, so just bail
                return defaultValue;
            } else {
                // get the multiplier for our input suffix
                //  and do the inverse of that multiplier to get the base value
                const multiplier = UnitService.defaultMultiplier(matchingUnitGroup.multiplier);
                baseValue = baseValue.times(new Decimal(multiplier));
            }
        }

        const newUnits = UnitService.assumeMaxValues(units);
        let convertedValue;
        // traverse our list of possible units, finding the first one (ordered)
        //  within its max value range, or one which has no max value specified
        for (let i = 0; i < newUnits.values.length; i++) {
            const unitGroup = newUnits.values[i];
            const multiplier = UnitService.defaultMultiplier(unitGroup.multiplier);
            convertedValue = baseValue.dividedBy(new Decimal(multiplier));
            suffix = unitGroup.suffix;

            // If the value is less than the max_value then we don't need to convert.
            // If no max_value is set, then this is likely the last unit in the list, so we will use it.
            if (!unitGroup.max_value || convertedValue.lessThan(new Decimal(unitGroup.max_value))) {
                break;
            }
        }

        if (isNegative) {
            convertedValue = new Decimal(0).minus(convertedValue);
        }

        if (decimals === null) {
            const precision = UnitService.getPrecision(convertedValue.toNumber());
            value = parseFloat(convertedValue.toPrecision(precision));
        } else {
            value = convertedValue.toFixed(decimals);
        }

        return {
            value,
            unit: suffix,
        };
    }

    private static assumeMaxValues(units: FieldSchemaUnits): FieldSchemaUnits {
        const newUnits = _.clone(units);

        if (newUnits && newUnits.values) {
            const values = _.orderBy(newUnits.values, ['multiplier'], ['asc']);

            for (let i = 0; i < values.length - 1; ++i) {
                if (!values[i].max_value) {
                    values[i].max_value =
                        UnitService.defaultMultiplier(values[i + 1].multiplier) /
                        UnitService.defaultMultiplier(values[i].multiplier);
                }
            }

            newUnits.values = values;
        }

        return newUnits;
    }

    private static defaultMultiplier(value) {
        // it's okay to provide no multiplier I think.... we'll assume 1
        return _.isNil(value) ? 1 : value;
    }

    private static getPrecision(value: number, minimum: number = 4) {
        if (value >= 1) {
            // This used to be the number of digits before the decimal point, but that was not enough precision
            // for values with large multipliers. Now we include up to 3 decimal places.
            return Math.max(value.toString().split('.')[0].length + 3, minimum);
        } else {
            return minimum;
        }
    }

    public static getFixed(value: number, minimum: number = 2) {
        if (Number.isInteger(value)) {
            return minimum;
        }

        const decimals = value.toString().split('.')[1];

        // deal with an edge case where if value is Number.MAX_VALUE or similar it is not an integer AND has no decimals.
        if (!decimals) {
            return minimum;
        }

        let i = 0;
        for (; i < decimals.length; i++) {
            if (decimals[i] !== '0') {
                i++;
                break;
            }
        }

        return Math.max(minimum, i);
    }

    // Replace the stat definition field units with anything set in the view.
    // This is not a merge! They must be overwritten to avoid adding more units.
    public static overWriteFieldSchemaUnits(
        view: View,
        statDefinition: StatDefinition,
        schemaName: string,
    ): StatDefinition {
        if (!view || !view.tables) {
            return statDefinition;
        }

        const tables = view.tables[schemaName];

        if (!tables || !tables.field_schemas) {
            return statDefinition;
        }

        Object.keys(tables.field_schemas).forEach((field) => {
            if (statDefinition.field_schemas[field] && tables.field_schemas[field].units) {
                statDefinition.field_schemas[field].units = tables.field_schemas[field].units;
            }
        });

        return statDefinition;
    }
}
